import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Layers, Trash2, AlertTriangle, TrendingUp } from "lucide-react";
import AllergeniBadges from "../AllergeniBadges";
import { formatEuro, formatPercentuale, crownRank } from "../../pages/menu/menuHelpers";
import { DeltaBadge } from "../StoricoVariazioniDialog";

export default function MenuProdottoCard({
  prod,
  categoria,
  rank,
  onOpen,
  onDelete,
  onToggleAttivo,
  alert,
  onOpenStorico,
}) {
  // Valori LIVE dal BACKEND (già IVA inclusa). La scheda mostra ESATTAMENTE gli stessi
  // numeri del dettaglio: food_cost_live (solo cibo) e totale_live (cibo + packaging).
  // MAI calcolo IVA nel frontend e MAI fallback a prod.food_cost (dato salvato/vecchio).
  const foodCostEff = prod.food_cost_live ?? 0;
  const totaleEff = prod.totale_live ?? 0;
  const margineEff = prod.margine_live ?? 0;
  const hasLive = prod.totale_live !== undefined && prod.totale_live !== null;
  const moltiplicatore =
    totaleEff > 0 ? (prod.prezzo_vendita / totaleEff).toFixed(1) : null;
  // Delta food cost vs baseline salvato (mostra anche i miglioramenti, es. -22.4%)
  const fcDelta = alert ? alert.delta_pct : (hasLive ? prod.food_cost_delta_pct : null);
  const showFcDelta = fcDelta != null && Math.abs(fcDelta) >= 1;
  const margineBaseline = alert ? alert.margine_salvato : prod.margine_percentuale;
  const margDelta = (showFcDelta && margineBaseline != null) ? (margineEff - margineBaseline) : null;
  const moltDelta = alert && alert.moltiplicatore_salvato ? ((alert.moltiplicatore_attuale - alert.moltiplicatore_salvato) / alert.moltiplicatore_salvato * 100) : null;

  const margineBgClass = (() => {
    if (!prod.attivo) return "bg-gray-100";
    if (margineEff >= 70) return "bg-green-100";
    if (margineEff >= 50) return "bg-emerald-100";
    if (margineEff >= 30) return "bg-yellow-100";
    if (margineEff >= 15) return "bg-orange-100";
    return "bg-red-100";
  })();
  const margineLblClass = (() => {
    if (!prod.attivo) return "text-gray-500";
    if (margineEff >= 70) return "text-green-600";
    if (margineEff >= 50) return "text-emerald-600";
    if (margineEff >= 30) return "text-yellow-600";
    if (margineEff >= 15) return "text-orange-600";
    return "text-red-600";
  })();
  const margineValClass = (() => {
    if (!prod.attivo) return "text-gray-600";
    if (margineEff >= 70) return "text-green-700";
    if (margineEff >= 50) return "text-emerald-700";
    if (margineEff >= 30) return "text-yellow-700";
    if (margineEff >= 15) return "text-orange-700";
    return "text-red-700";
  })();

  return (
    <Card
      className={`bg-white transition-all cursor-pointer relative active:scale-[0.98] ${
        prod.attivo ? "hover:shadow-lg" : "opacity-60 grayscale-[30%] bg-gray-50"
      } ${rank ? "ring-2 ring-amber-300 ring-offset-1" : ""} ${alert ? "border-l-4 border-l-amber-500" : ""}`}
      onClick={() => onOpen(prod)}
      data-testid={`prodotto-card-${prod.id}`}
    >
      {rank && (
        <div
          className="absolute -top-2 -left-2 bg-amber-500 text-white text-xs font-black px-2 py-1 rounded-lg shadow flex items-center gap-1 z-10"
          data-testid={`badge-top-margine-${prod.id}`}
          title={`Top ${rank} per margine`}
        >
          <span className="text-sm">{crownRank(rank)}</span>
          <span>Top margine</span>
        </div>
      )}
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start gap-2 mb-1">
          <h3 className={`font-bold text-lg leading-tight ${prod.attivo ? "text-gray-900" : "text-gray-500"}`}>
            {prod.nome}
          </h3>
          <Button
            size="sm"
            variant="ghost"
            className="h-10 w-10 sm:h-7 sm:w-7 p-0 text-gray-300 hover:text-red-500 hover:bg-red-50 shrink-0 -mt-1 -mr-1"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(prod);
            }}
            data-testid={`elimina-btn-${prod.id}`}
          >
            <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
          </Button>
        </div>

        {/* Categoria + Ingredienti */}
        <div className={`flex items-center gap-2 text-sm mb-2 ${prod.attivo ? "text-gray-500" : "text-gray-400"}`}>
          {categoria && (
            <span>
              {categoria.icona} {categoria.nome}
            </span>
          )}
          {categoria && <span>•</span>}
          <span className="flex items-center gap-1">
            <Layers className="w-3 h-3" />
            {prod.componenti?.length || 0} ingredienti
          </span>
        </div>

        {/* Alert food cost ancorato al piatto */}
        {alert && (
          <div
            className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3"
            onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(prod); }}
            data-testid={`menu-item-alert-banner-${prod.id}`}
          >
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-amber-800 flex-1">
              Food cost <b>{(alert.delta_pct || 0) >= 0 ? "+" : ""}{(alert.delta_pct || 0).toFixed(1)}%</b>: da {formatEuro(alert.food_cost_salvato)} a {formatEuro(alert.food_cost_attuale)} · margine {formatPercentuale(alert.margine_salvato)}→{formatPercentuale(alert.margine_attuale)}
            </span>
            <TrendingUp className="h-4 w-4 text-amber-600 flex-shrink-0" />
          </div>
        )}

        {/* Allergeni HACCP */}
        <div className="mb-4">
          <AllergeniBadges
            allergeni={prod.allergeni}
            nonVerificati={prod.allergeni_non_verificati}
            calcolabile={prod.allergeni_calcolabile !== false}
          />
        </div>

        {/* Prezzo */}
        <div className="text-center py-4 mb-4 relative">
          <p className={`text-4xl font-bold ${prod.attivo ? "text-[#FF6584]" : "text-gray-400"}`}>
            {formatEuro(prod.prezzo_vendita)}
          </p>
          <p className="text-xs text-gray-400 mt-1">Prezzo di vendita</p>
          <button
            onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(prod); }}
            className="absolute top-2 right-2 h-7 w-7 rounded-md flex items-center justify-center text-gray-300 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            title="Storico food cost e margini"
            data-testid={`open-history-dialog-btn-${prod.id}`}
          >
            <TrendingUp className="h-4 w-4" />
          </button>
        </div>

        {/* Stats */}
        <div className="flex justify-center gap-2 flex-wrap mb-4">
          <div className={`flex flex-col items-center px-3 py-2 rounded-lg ${prod.attivo ? "bg-emerald-50" : "bg-gray-100"}`}>
            <span className={`text-xs ${prod.attivo ? "text-emerald-600" : "text-gray-500"}`}>Food Cost</span>
            <span className={`font-bold ${prod.attivo ? "text-emerald-700" : "text-gray-600"}`}>
              {formatEuro(foodCostEff)}
            </span>
          </div>
          <div className="flex flex-col items-center px-3 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
            onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(prod); }}
            title="Vedi storico food cost" data-testid={`menu-foodcost-stat-${prod.id}`}>
            <span className="text-xs text-gray-500">Totale</span>
            <span className={`font-bold ${prod.attivo ? "text-gray-700" : "text-gray-600"}`}>
              {formatEuro(totaleEff)}
            </span>
            {showFcDelta && <div className="mt-1"><DeltaBadge delta={fcDelta} /></div>}
          </div>
          <div className={`flex flex-col items-center px-3 py-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity ${margineBgClass}`}
            onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(prod); }}
            title="Vedi storico margine" data-testid={`menu-margine-stat-${prod.id}`}>
            <span className={`text-xs ${margineLblClass}`}>Margine</span>
            <span className={`font-bold ${margineValClass}`}>
              {formatPercentuale(margineEff)}
            </span>
            {margDelta != null && <div className="mt-1"><DeltaBadge delta={margDelta} invert /></div>}
          </div>
          {moltiplicatore && (
            <div className={`flex flex-col items-center px-3 py-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity ${prod.attivo ? "bg-blue-100" : "bg-gray-100"}`}
              onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(prod); }}
              title="Vedi storico moltiplicatore" data-testid={`menu-moltiplicatore-stat-${prod.id}`}>
              <span className={`text-xs ${prod.attivo ? "text-blue-600" : "text-gray-500"}`}>Molt.</span>
              <span className={`font-bold ${prod.attivo ? "text-blue-700" : "text-gray-600"}`}>×{moltiplicatore}</span>
              {moltDelta != null && <div className="mt-1"><DeltaBadge delta={moltDelta} invert /></div>}
            </div>
          )}
        </div>

        {/* Toggle Attivo */}
        <div
          className="flex items-center justify-between pt-3 border-t border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          <span className={`text-sm font-medium ${prod.attivo ? "text-primary" : "text-gray-400"}`}>
            {prod.attivo ? "Attivo" : "Non attivo"}
          </span>
          <button
            onClick={() => onToggleAttivo(prod, !prod.attivo)}
            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              prod.attivo ? "bg-primary focus:ring-primary" : "bg-gray-300 focus:ring-gray-400"
            }`}
            data-testid={`toggle-attivo-${prod.id}`}
          >
            <span
              className={`inline-block h-5 w-5 sm:h-4 sm:w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                prod.attivo ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

